/**
 * Importar extractos bancarios.
 *
 * La pantalla está construida en tres pasos, y el orden importa: **primero se ve
 * lo que se va a hacer, después se hace**. El usuario sube el archivo, ve cómo se
 * interpretaron las columnas y las primeras filas, corrige lo que haga falta, y
 * solo entonces importa.
 *
 * Importar 300 movimientos a ciegas con el mapeo equivocado es el desastre clásico
 * de esta función, y se evita enseñando el resultado antes.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, FileSpreadsheet, RotateCcw, TriangleAlert, Upload } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

import { formatDateLabel } from '@eldescuadre/core';

import {
  getAccounts,
  getCategories,
  getImportBatches,
  importCommit,
  importPreview,
  undoImport,
} from '../lib/endpoints';
import { ApiError } from '../lib/api';
import { Card, EmptyState, ErrorNote, Field, Loading, Money, Note, SubmitButton } from '../components/ui';

/** Nombres en cristiano de cada campo del mapeo. */
const FIELD_LABELS: Record<string, string> = {
  date: 'Fecha',
  amount: 'Importe (con signo)',
  debit: 'Débito (lo que sale)',
  credit: 'Crédito (lo que entra)',
  description: 'Descripción',
  balance: 'Saldo',
  ignore: 'Ignorar',
};

const FIELD_OPTIONS = ['ignore', 'date', 'amount', 'debit', 'credit', 'description', 'balance'];

type Step = 'subir' | 'revisar' | 'listo';

export function ImportPage(): React.JSX.Element {
  const queryClient = useQueryClient();
  const [step, setStep] = useState<Step>('subir');
  const [content, setContent] = useState('');
  const [fileName, setFileName] = useState('');
  const [hasHeader, setHasHeader] = useState(true);
  const [mapping, setMapping] = useState<string[]>([]);
  const [accountId, setAccountId] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [result, setResult] = useState<{
    imported: number;
    duplicates: number;
    problems: number;
    batchId: string;
  } | null>(null);

  const accounts = useQuery({ queryKey: ['accounts'], queryFn: () => getAccounts() });
  const categories = useQuery({
    queryKey: ['categories', 'EXPENSE'],
    queryFn: () => getCategories('EXPENSE'),
  });
  const batches = useQuery({ queryKey: ['import', 'batches'], queryFn: getImportBatches });

  const preview = useQuery({
    queryKey: ['import', 'preview', content, hasHeader],
    queryFn: () => importPreview(content, hasHeader),
    enabled: content.length > 0 && step === 'revisar',
  });

  // Al llegar la propuesta del servidor, se copia al estado editable.
  const suggested = preview.data?.mapping;
  useEffect(() => {
    if (suggested && mapping.length === 0) setMapping([...suggested]);
  }, [suggested, mapping.length]);

  const chosenAccountId = accountId || accounts.data?.accounts[0]?.id || '';

  /** Problemas que impiden importar. */
  const problem = useMemo(() => {
    if (!content) return 'Sube un archivo primero.';
    if (!preview.data) return null;
    if (!mapping.includes('date')) return 'Indica qué columna tiene la fecha.';
    if (!mapping.includes('amount') && !mapping.includes('debit') && !mapping.includes('credit')) {
      return 'Indica qué columna tiene el importe (o las de débito y crédito).';
    }
    if (!chosenAccountId) return 'Elige a qué cuenta pertenece el extracto.';
    if (preview.data.validRows === 0) return 'Ninguna fila del archivo se pudo interpretar.';
    return null;
  }, [content, preview.data, mapping, chosenAccountId]);

  const commit = useMutation({
    mutationFn: () =>
      importCommit({
        content,
        hasHeader,
        mapping: mapping as Parameters<typeof importCommit>[0]['mapping'],
        accountId: chosenAccountId,
        defaultCategoryId: categoryId || undefined,
        skipDuplicates: true,
        fileName: fileName || undefined,
      }),
    onSuccess: async (data) => {
      setResult({
        imported: data.imported,
        duplicates: data.duplicates,
        problems: data.problems,
        batchId: data.batchId,
      });
      setStep('listo');
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['transactions'] }),
        queryClient.invalidateQueries({ queryKey: ['accounts'] }),
        queryClient.invalidateQueries({ queryKey: ['summary'] }),
        queryClient.invalidateQueries({ queryKey: ['import'] }),
      ]);
    },
  });

  const undo = useMutation({
    mutationFn: undoImport,
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['transactions'] }),
        queryClient.invalidateQueries({ queryKey: ['accounts'] }),
        queryClient.invalidateQueries({ queryKey: ['summary'] }),
        queryClient.invalidateQueries({ queryKey: ['import'] }),
      ]);
    },
  });

  const error = commit.error instanceof ApiError ? commit.error : null;

  /** Lee el archivo elegido como texto, sin subirlo a ningún sitio todavía. */
  const readFile = async (file: File): Promise<void> => {
    const text = await file.text();
    setContent(text);
    setFileName(file.name);
    setMapping([]);
    setStep('revisar');
  };

  const reset = (): void => {
    setContent('');
    setFileName('');
    setMapping([]);
    setResult(null);
    setStep('subir');
  };

  return (
    <div className="space-y-4 animate-ed-fade-in">
      <header>
        <h1 className="font-display text-2xl font-extrabold tracking-tight">Importar extracto</h1>
        <p className="mt-0.5 text-sm text-ed-text-muted">
          Sube el archivo del banco y trae tu histórico de una vez.
        </p>
      </header>

      {/* Paso 1: subir */}
      {step === 'subir' && (
        <>
          <Card>
            <label className="flex cursor-pointer flex-col items-center gap-3 rounded-ed border-2 border-dashed border-ed-bg-border/50 px-4 py-10 text-center transition-colors hover:border-ed-gold/50">
              <FileSpreadsheet size={36} className="text-ed-text-muted" aria-hidden="true" />
              <span className="font-display font-bold">Elige el archivo del banco</span>
              <span className="max-w-sm text-xs text-ed-text-muted">
                Archivos CSV o de texto separado por comas, punto y coma o tabuladores. Si tu banco
                solo da Excel, ábrelo y guárdalo como CSV.
              </span>
              <span className="ed-btn-secondary !w-auto !px-4">
                <Upload size={16} aria-hidden="true" />
                Elegir archivo
              </span>
              <input
                type="file"
                accept=".csv,.txt,text/csv,text/plain"
                className="sr-only"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) void readFile(file);
                }}
              />
            </label>
          </Card>

          <Note>
            El archivo <strong>no se sube a ningún sitio</strong> hasta que confirmes la
            importación. Primero se lee aquí y te enseñamos qué se entendió.
          </Note>
        </>
      )}

      {/* Paso 2: revisar */}
      {step === 'revisar' && (
        <>
          <Card
            title="Archivo"
            action={
              <button type="button" className="text-xs text-ed-cyan-light hover:underline" onClick={reset}>
                Cambiar
              </button>
            }
          >
            <p className="truncate text-sm">{fileName || 'Archivo sin nombre'}</p>
            {preview.isLoading && <Loading label="Leyendo el archivo…" />}

            {preview.data && (
              <div className="mt-3 grid grid-cols-3 gap-3 text-center">
                <div className="rounded-ed border border-ed-bg-border/30 bg-ed-bg-elevated/40 p-2">
                  <p className="text-xs text-ed-text-muted">Filas</p>
                  <p className="font-display font-bold">{preview.data.totalRows}</p>
                </div>
                <div className="rounded-ed border border-ed-success/30 bg-ed-success/10 p-2">
                  <p className="text-xs text-ed-text-muted">Se entienden</p>
                  <p className="font-display font-bold text-ed-success">{preview.data.validRows}</p>
                </div>
                <div
                  className={`rounded-ed border p-2 ${
                    preview.data.problemRows > 0
                      ? 'border-ed-coral-deep/40 bg-ed-coral-deep/10'
                      : 'border-ed-bg-border/30 bg-ed-bg-elevated/40'
                  }`}
                >
                  <p className="text-xs text-ed-text-muted">Con problemas</p>
                  <p
                    className={`font-display font-bold ${
                      preview.data.problemRows > 0 ? 'text-ed-coral-light' : ''
                    }`}
                  >
                    {preview.data.problemRows}
                  </p>
                </div>
              </div>
            )}
          </Card>

          {/* El mapeo de columnas: lo que el usuario puede corregir. */}
          {preview.data && (
            <Card title="Columnas">
              <p className="mb-3 text-xs text-ed-text-muted">
                Esto es lo que hemos entendido. Si algo está mal, cámbialo antes de importar.
              </p>
              <ul className="space-y-2">
                {preview.data.headers.map((header, index) => (
                  <li key={`${header}-${index}`} className="flex items-center gap-2">
                    <span className="min-w-0 flex-1 truncate text-sm">{header || `Columna ${index + 1}`}</span>
                    <select
                      className="ed-input !w-auto"
                      value={mapping[index] ?? 'ignore'}
                      onChange={(e) =>
                        setMapping((current) => {
                          const next = [...current];
                          next[index] = e.target.value;
                          return next;
                        })
                      }
                      aria-label={`Qué es la columna ${header}`}
                    >
                      {FIELD_OPTIONS.map((option) => (
                        <option key={option} value={option}>
                          {FIELD_LABELS[option]}
                        </option>
                      ))}
                    </select>
                  </li>
                ))}
              </ul>

              <label className="mt-3 flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-ed-bg-border"
                  checked={hasHeader}
                  onChange={(e) => {
                    setHasHeader(e.target.checked);
                    setMapping([]);
                  }}
                />
                La primera fila son los títulos
              </label>
            </Card>
          )}

          {/* Vista previa de las primeras filas ya interpretadas. */}
          {preview.data && preview.data.rows.length > 0 && (
            <Card title="Cómo quedarían">
              <ul className="divide-y divide-ed-bg-border/25">
                {preview.data.rows.map((row) => (
                  <li key={row.line} className="flex items-start justify-between gap-3 py-2">
                    <span className="min-w-0">
                      <span className="block truncate text-sm">{row.description}</span>
                      <span className="block text-xs text-ed-text-muted">
                        {row.date ? formatDateLabel(row.date) : 'sin fecha'}
                      </span>
                      {row.problems.length > 0 && (
                        <span className="mt-0.5 flex items-center gap-1 text-xs text-ed-coral-light">
                          <TriangleAlert size={12} aria-hidden="true" />
                          {row.problems.join(' ')}
                        </span>
                      )}
                    </span>
                    {row.amount !== null ? (
                      <Money
                        amount={row.amount}
                        signed
                        className="shrink-0 text-sm font-semibold"
                      />
                    ) : (
                      <span className="shrink-0 text-xs text-ed-text-muted">—</span>
                    )}
                  </li>
                ))}
              </ul>
              {preview.data.totalRows > preview.data.rows.length && (
                <p className="mt-2 text-xs text-ed-text-muted">
                  Se muestran las primeras {preview.data.rows.length} de {preview.data.totalRows}{' '}
                  filas.
                </p>
              )}
            </Card>
          )}

          {/* Destino */}
          <Card title="¿A qué cuenta y categoría?">
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Cuenta" htmlFor="cuenta-import">
                <select
                  id="cuenta-import"
                  className="ed-input"
                  value={chosenAccountId}
                  onChange={(e) => setAccountId(e.target.value)}
                >
                  <option value="">Elige una cuenta…</option>
                  {(accounts.data?.accounts ?? []).map((account) => (
                    <option key={account.id} value={account.id}>
                      {account.name}
                    </option>
                  ))}
                </select>
              </Field>

              <Field
                label="Categoría (opcional)"
                htmlFor="categoria-import"
                hint="Se la pone a todo. Después puedes afinarla."
              >
                <select
                  id="categoria-import"
                  className="ed-input"
                  value={categoryId}
                  onChange={(e) => setCategoryId(e.target.value)}
                >
                  <option value="">Sin categoría</option>
                  {(categories.data?.categories ?? []).map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </Field>
            </div>

            <p className="mt-3 text-xs text-ed-text-muted">
              Lo que resta en el extracto entra como <strong>gasto</strong> y lo que suma como{' '}
              <strong>ingreso</strong>, tal y como lo dice el banco. Los movimientos que ya tenías
              se saltan solos: puedes subir el mismo archivo dos veces sin miedo.
            </p>
          </Card>

          {problem && <ErrorNote message={problem} />}
          {error && (
            <ErrorNote
              message={error.message}
              hint={
                error.status === 409
                  ? 'Puede que ya hayas importado este mismo archivo.'
                  : 'Revisa el mapeo de las columnas.'
              }
            />
          )}

          <SubmitButton
            type="button"
            pending={commit.isPending}
            disabled={problem !== null}
            onClick={() => commit.mutate()}
          >
            Importar {preview.data ? `${preview.data.validRows} movimientos` : ''}
          </SubmitButton>
        </>
      )}

      {/* Paso 3: hecho */}
      {step === 'listo' && result && (
        <>
          <Card>
            <div className="flex flex-col items-center gap-3 py-4 text-center">
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-ed-success/15 text-ed-success">
                <CheckCircle2 size={28} aria-hidden="true" />
              </span>
              <p className="font-display text-lg font-bold">
                {result.imported} {result.imported === 1 ? 'movimiento' : 'movimientos'} importados
              </p>
              {result.duplicates > 0 && (
                <p className="text-sm text-ed-text-silver">
                  Se saltaron {result.duplicates} que ya tenías.
                </p>
              )}
              {result.problems > 0 && (
                <p className="text-sm text-ed-coral-light">
                  {result.problems} {result.problems === 1 ? 'fila' : 'filas'} no se pudieron leer y
                  se quedaron fuera.
                </p>
              )}
              <p className="text-xs text-ed-text-muted">
                El saldo de la cuenta ya está actualizado. Si algo salió mal, puedes deshacerlo
                entero desde abajo.
              </p>
            </div>
          </Card>

          <SubmitButton type="button" variant="secondary" onClick={reset}>
            Importar otro archivo
          </SubmitButton>
        </>
      )}

      {/* Historial de importaciones, con la opción de deshacer. */}
      {batches.data && batches.data.batches.length > 0 && (
        <Card title="Importaciones hechas">
          <ul className="divide-y divide-ed-bg-border/25">
            {batches.data.batches.map((batch) => (
              <li key={batch.id} className="flex items-center justify-between gap-3 py-2.5">
                <span className="min-w-0">
                  <span className="block truncate text-sm">
                    {batch.fileName || 'Archivo sin nombre'}
                    {batch.status === 'UNDONE' && (
                      <span className="ml-2 rounded bg-ed-coral-deep/30 px-1.5 py-0.5 text-[10px] uppercase text-ed-coral-light">
                        deshecha
                      </span>
                    )}
                  </span>
                  <span className="block text-xs text-ed-text-muted">
                    {formatDateLabel(batch.createdAt.slice(0, 10))} · {batch.imported} movimientos
                    {batch.accountName && <> · {batch.accountName}</>}
                    {batch.duplicates > 0 && <> · {batch.duplicates} repetidos</>}
                  </span>
                </span>

                {batch.status === 'APPLIED' && (
                  <button
                    type="button"
                    className="ed-btn-ghost !min-h-[36px] !w-auto shrink-0 !px-3 text-xs text-ed-coral-light"
                    disabled={undo.isPending}
                    onClick={() => {
                      if (
                        window.confirm(
                          `¿Deshacer esta importación? Se borrarán sus ${batch.imported} movimientos. Los que hayas editado después se conservan.`,
                        )
                      ) {
                        undo.mutate(batch.id);
                      }
                    }}
                  >
                    <RotateCcw size={13} aria-hidden="true" />
                    Deshacer
                  </button>
                )}
              </li>
            ))}
          </ul>

          {undo.isError && (
            <div className="mt-3">
              <ErrorNote message="No se pudo deshacer la importación." />
            </div>
          )}
        </Card>
      )}

      {batches.data && batches.data.batches.length === 0 && step === 'subir' && (
        <Card>
          <EmptyState
            title="Todavía no has importado nada"
            message="Cuando importes tu primer extracto, aquí quedará el registro para poder deshacerlo si algo sale mal."
          />
        </Card>
      )}
    </div>
  );
}
